import { useCallback, useEffect } from 'react';

import dayjs from 'dayjs';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { getListConversations, getListHistory, readMessages } from 'api/endpoints/chat';
import { ENUM_ROLE_SOCKET } from 'constants/enum';
import { useSocket } from 'hooks/useSocket';
import {
  setConversations,
  setDataCurrentConversation,
  setIsScrollToBottomListChat,
  setMessagesHistory,
  setPaginationConversations,
  setPaginationMessagesHistory,
} from 'store/actions/chat';
import { convertToQueryString, groupMessageDate } from 'utils/helper';

import { useNetwork } from './useNetwork';

export const useChat = () => {
  const dispatch = useDispatch();

  const { me } = useSelector((state) => state.auth);
  const { paginationConversations, currentConversation, paginationMessagesHistory, messagesHistory } = useSelector((state) => state.chat);

  const { isOnline } = useNetwork();
  const { isConnected, connectSocket, sendMessage, onMessage } = useSocket({
    id_user: me?.id,
    role: ENUM_ROLE_SOCKET.ADMIN,
    sender_name: me?.name || 'Admin',
  });

  // READ MESSAGES
  const _readMessages = async (conversationId) => {
    if (!me || !conversationId) return;
    const queryString = convertToQueryString({ conversation_id: conversationId });
    await readMessages(queryString);
  };

  // GET CONVERSATIONS
  const getConversations = async (idConversation = null) => {
    if (!me || !isOnline) return;

    if (idConversation) {
      _readMessages(idConversation);
    }

    let _conversations = [];
    let total = 0;

    for (let i = 0; i < paginationConversations.page; i++) {
      const queryString = convertToQueryString({ page: i + 1, page_size: paginationConversations.page_size });
      const response = await getListConversations(queryString);
      _conversations = [..._conversations, ...response.data];
      total = response.total;
    }

    dispatch(setPaginationConversations({ ...paginationConversations, total: total || 0 }));

    const conversationAddedUnread = _.cloneDeep(_conversations);
    conversationAddedUnread.forEach((conversation) => {
      if (conversation.conversation_id === currentConversation) {
        conversation.is_unread = false;
      }
    });

    if (idConversation) {
      const conversation = conversationAddedUnread?.find((conversation) => conversation.conversation_id === idConversation);
      dispatch(setDataCurrentConversation(conversation || null));
    }

    dispatch(setConversations(_conversations));
    dispatch(setIsScrollToBottomListChat(false));
  };

  const _getConversations = useCallback(async () => {
    await getConversations();
  }, [me, paginationConversations.page, isOnline]);

  useEffect(() => {
    _getConversations();
  }, [_getConversations]);

  // GET MESSAGES
  const getMessagesHistory = async () => {
    if (!me || !currentConversation) return;

    const queryString = convertToQueryString({ page: paginationMessagesHistory.page, page_size: paginationMessagesHistory.page_size });
    const response = await getListHistory(currentConversation, queryString);
    const messagesGrouped = await groupMessageDate(response.data, messagesHistory);

    dispatch(setMessagesHistory(messagesGrouped));
    dispatch(setPaginationMessagesHistory({ ...paginationMessagesHistory, total: response.total }));
  };

  const _getMessagesHistory = useCallback(async () => {
    await getMessagesHistory();
  }, [me, currentConversation, paginationMessagesHistory.page, paginationMessagesHistory.page_size]);

  useEffect(() => {
    _getMessagesHistory();
  }, [_getMessagesHistory]);

  // CONNECT SOCKET
  useEffect(() => {
    if (me?.id && !isConnected && isOnline) {
      connectSocket();
    }
  }, [me?.id, isConnected, isOnline, connectSocket]);

  // AUTO RECONNECT WHEN LOST CONNECTION
  useEffect(() => {
    if (me?.id && !isConnected) {
      const timeoutId = setTimeout(() => {
        if (!isConnected && me?.id) {
          connectSocket();
        }
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [me?.id, isConnected, connectSocket]);

  const receiveMessage = useCallback(() => {
    if (!isConnected) {
      return;
    }

    onMessage(async (message) => {
      const date = dayjs().format('YYYY/MM/DD');
      await getConversations(currentConversation);
      if (currentConversation === message.data.conversation_id) {
        dispatch((dispatch, getState) => {
          const currentMessagesHistory = getState().chat.messagesHistory;
          const updatedMessagesHistory = { ...currentMessagesHistory };

          if (!updatedMessagesHistory[date]) {
            updatedMessagesHistory[date] = [];
          } else {
            updatedMessagesHistory[date] = [...updatedMessagesHistory[date]];
          }
          updatedMessagesHistory[date].push(message.data);
          dispatch(setMessagesHistory(updatedMessagesHistory));
        });
      }
    });
  }, [isConnected, onMessage, getConversations, dispatch]);

  useEffect(() => {
    receiveMessage();
  }, [receiveMessage]);

  return {
    isConnected,
    sendMessage,
    getConversations,
    getMessagesHistory,
    connectSocket,
    readMessages: _readMessages,
  };
};
